import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
// Type-only: teaches `components` about the Data Grid's slots so the grid's density can be a
// theme decision like every other component's. No runtime import, no bundle cost.
import type {} from '@mui/x-data-grid/themeAugmentation';
import {
  brand,
  darkText,
  density,
  elevation,
  link,
  navChrome,
  neutral,
  radius,
  selection,
  semantic,
  shadowInk,
  shadows,
  spacingUnit,
  statusDark,
  statusLight,
  typographyTokens,
  warningAlert,
} from './tokens';

import './augmentation';
import type { ChromeTokens } from './variant';

/**
 * The EIS console theme — the `berry` chrome variant, wearing the **EIS Design System 2025**
 * (palette A, design v2; T-31 amending T-8: tokens and shell layout only, components stay MUI +
 * `@eis/patterns`).
 *
 * CSS variables are on, so light/dark switch without a React re-render and Storybook/Playwright
 * can flip schemes by toggling one attribute. `size="small"` is the default for every input,
 * button and table — density is a property of the theme, not something each screen re-decides,
 * and it is the one part of the bootstrap theme the restyle deliberately keeps (CLAUDE.md rule 4).
 */

/**
 * Density defaults that are NOT a look: `size="small"` everywhere, arrows on tooltips, compact
 * tabs and grid rows (CLAUDE.md rule 4). Both chrome variants spread these, so a variant is only
 * ever colours, radii, fonts and chrome sizes — never a different console.
 */
export const densityComponentDefaults: NonNullable<ThemeOptions['components']> = {
  MuiButton: { defaultProps: { size: 'small', disableElevation: true } },
  MuiButtonGroup: { defaultProps: { size: 'small', disableElevation: true } },
  MuiToggleButtonGroup: { defaultProps: { size: 'small' } },
  MuiToggleButton: { defaultProps: { size: 'small' } },
  MuiIconButton: { defaultProps: { size: 'small' } },
  MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
  MuiSelect: { defaultProps: { size: 'small' } },
  MuiFormControl: { defaultProps: { size: 'small' } },
  MuiAutocomplete: { defaultProps: { size: 'small' } },
  MuiCheckbox: { defaultProps: { size: 'small' } },
  MuiRadio: { defaultProps: { size: 'small' } },
  MuiSwitch: { defaultProps: { size: 'small' } },
  MuiChip: { defaultProps: { size: 'small' } },
  MuiTable: { defaultProps: { size: 'small' } },
  MuiTooltip: { defaultProps: { arrow: true } },
  MuiTabs: { styleOverrides: { root: { minHeight: 40 } } },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 600, minHeight: 40, paddingBlock: 8 },
    },
  },
  MuiDialog: { defaultProps: { fullWidth: true, maxWidth: 'sm' } },
  MuiLink: { defaultProps: { underline: 'hover' } },
  MuiSkeleton: { defaultProps: { animation: 'wave' } },
  MuiPaper: { defaultProps: { elevation: 0 } },
  /**
   * Grid density as a theme decision (prompt 10.1). The visual treatment stays in `dataGridSx`,
   * which grids spread — MUI X's slot overrides cannot express the header/cell rules without
   * duplicating them here, and one source is worth more than theme purity.
   */
  MuiDataGrid: {
    defaultProps: {
      density: 'compact',
      rowHeight: density.gridRowHeight,
      columnHeaderHeight: density.gridHeaderHeight,
      disableRowSelectionOnClick: true,
    },
  },
};

/** The `berry` chrome: what the shell and the patterns lay out with in this variant. */
export const berryChrome: ChromeTokens = {
  variant: 'berry',
  navWidth: density.navWidth,
  navWidthCollapsed: density.navWidthCollapsed,
  headerHeight: density.toolbarHeight,
  lightOnly: false,
  surface: {
    cardRadius: radius.surface,
    cardShadow: elevation.card,
    cardBorder: false,
    controlRadius: radius.control,
    badgeRadius: radius.badge,
    sectionAccentRule: false,
    headerBand: false,
    headerCard: true,
    filterBarTint: false,
    filterStyle: 'selects',
    fieldValue: { fontSize: '0.875rem', fontWeight: 600 },
    grid: {
      frame: false,
      headerBackground: 'var(--mui-palette-background-paper)',
      headerColor: 'var(--mui-palette-text-secondary)',
      headerFontSize: '0.78125rem',
      cellFontSize: '0.875rem',
      rowBorder: 'var(--mui-palette-surfaces-hairline)',
    },
  },
};

/** The class `StatusChip` puts on its chip, so the theme can draw design v2's tonal chip. */
export const STATUS_CHIP_CLASS = 'eis-status-chip';

/** `rgb(<channels> / a)` of a palette colour, through MUI's generated channel variables. */
const channel = (variable: string, alpha: number): string =>
  `rgb(var(--mui-palette-${variable}Channel) / ${String(alpha)})`;

const berryComponents: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      // The per-scheme shadow ink the elevation ramp is written against (tokens/elevation.ts),
      // and the link colour (the AA substitute for the design's `#007BFF`).
      ':root': { '--eis-shadow-ink': shadowInk.light, '--eis-link': link.light },
      '[data-mui-color-scheme="dark"]': {
        '--eis-shadow-ink': shadowInk.dark,
        '--eis-link': link.dark,
      },
      // German UI copy is long; never let a label silently clip.
      body: { overflowWrap: 'anywhere' },
      // Open Sans ships 300/400/600 only; bold maps to 600 instead of a synthesised 700.
      'b, strong': { fontWeight: 600 },
      ':focus-visible': { outlineOffset: 2 },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 600, borderRadius: radius.control },
      sizeSmall: { paddingInline: 14, minHeight: 34 },
    },
    variants: [
      {
        // The design's secondary button: white fill, the input border, a grey label.
        props: { variant: 'outlined', color: 'primary' },
        style: {
          backgroundColor: 'var(--mui-palette-background-paper)',
          borderColor: 'var(--mui-palette-grey-300)',
          color: 'var(--mui-palette-text-secondary)',
          '&:hover': {
            backgroundColor: 'var(--mui-palette-background-default)',
            borderColor: 'var(--mui-palette-grey-400)',
          },
        },
      },
    ],
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: radius.control,
        /**
         * MUI paints an UNSELECTED toggle in `action.active` — an alpha over whatever is behind
         * it, which composites below 4.5:1 on the page grey. `text.secondary` is a declared value
         * the contrast suite asserts on every surface in both schemes.
         */
        color: 'var(--mui-palette-text-secondary)',
        '&.Mui-selected': {
          color: 'var(--mui-palette-surfaces-selectedText)',
          backgroundColor: 'var(--mui-palette-surfaces-selected)',
          '&:hover': { backgroundColor: 'var(--mui-palette-surfaces-selected)' },
        },
      },
    },
  },
  MuiIconButton: { styleOverrides: { root: { borderRadius: radius.control } } },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radius.control,
        backgroundColor: 'var(--mui-palette-background-paper)',
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${channel('primary-light', 0.25)}` },
      },
      notchedOutline: { borderColor: 'var(--mui-palette-grey-300)' },
    },
  },
  /**
   * Pill tabs (design v2): the strip is a white pill on the card shadow, the selected tab a
   * filled `primary.main` pill. Counts are the caller's trailing `<Box component="span">`.
   * The strip hugs its tabs; a caller that wants it full width passes `sx={{ width: '100%' }}`.
   */
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 0,
        width: 'fit-content',
        maxWidth: '100%',
        padding: 4,
        borderRadius: radius.pill,
        backgroundColor: 'var(--mui-palette-background-paper)',
        boxShadow: elevation.card,
      },
      indicator: { display: 'none' },
      list: { gap: 4 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 32,
        minWidth: 0,
        paddingBlock: 6,
        paddingInline: 14,
        borderRadius: radius.tab,
        textTransform: 'none',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.6,
        color: 'var(--mui-palette-primary-main)',
        '&:hover': { backgroundColor: channel('primary-light', 0.08) },
        '&.Mui-selected': {
          color: 'var(--mui-palette-primary-contrastText)',
          backgroundColor: 'var(--mui-palette-primary-main)',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      // Metadata badges.
      root: {
        borderRadius: radius.badge,
        fontWeight: 600,
        // Design v2's tonal status chip.
        [`&.${STATUS_CHIP_CLASS}`]: {
          height: 22,
          borderRadius: radius.chip,
          fontSize: '0.75rem',
          boxShadow: 'none',
          '& .MuiChip-label': { paddingInline: 7 },
        },
      },
    },
  },
  /** The design's card: no border, the soft card shadow, 5px corners. */
  MuiCard: {
    defaultProps: { elevation: 0, variant: 'elevation' },
    styleOverrides: {
      root: { borderRadius: radius.surface, boxShadow: elevation.card, border: 'none' },
    },
  },
  /**
   * A card title is the 18/600 card-title scale (`h3`) but stays a `<span>`: Typography maps
   * `variant="h3"` onto an `<h3>` element, and whether a card heads a document section is the
   * screen's call, not the theme's (axe heading-order).
   */
  MuiCardHeader: {
    defaultProps: { slotProps: { title: { variant: 'h3', component: 'span' } } },
    styleOverrides: { root: { padding: 20 } },
  },
  MuiCardContent: {
    styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
  },
  /**
   * Paper keeps the control radius (`shape.borderRadius`): AppBar and Drawer are Papers too, and
   * a rounded app bar is a bug. The container radius is applied by the components that are
   * surfaces — Card, Dialog, Menu, Popover, and the patterns through `theme.eis.surface`.
   */
  MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
  MuiDialog: {
    styleOverrides: { paper: { borderRadius: radius.surface, boxShadow: elevation.overlay } },
  },
  MuiMenu: {
    styleOverrides: { paper: { borderRadius: radius.surface, boxShadow: elevation.overlay } },
  },
  MuiPopover: {
    styleOverrides: { paper: { borderRadius: radius.surface, boxShadow: elevation.overlay } },
  },
  /** Soft alerts; the warning is the design's own pair. */
  MuiAlert: {
    defaultProps: { variant: 'standard' },
    styleOverrides: { root: { borderRadius: radius.surface } },
    variants: [
      {
        props: { variant: 'standard', severity: 'warning' },
        style: {
          backgroundColor: warningAlert.surface,
          color: warningAlert.text,
          '& .MuiAlert-icon': { color: warningAlert.text },
        },
      },
    ],
  },
  MuiLink: {
    variants: [{ props: { color: 'primary' }, style: { color: 'var(--eis-link)' } }],
  },
  MuiListItemButton: { styleOverrides: { root: { borderRadius: radius.control } } },
  /** The header is white with a hairline under it — no shadow. */
  MuiAppBar: {
    defaultProps: { elevation: 0, color: 'inherit' },
    styleOverrides: { root: { backgroundImage: 'none', boxShadow: elevation.chrome } },
  },
  MuiDrawer: {
    styleOverrides: { paper: { backgroundImage: 'none', borderRight: 'none' } },
  },
  MuiDivider: { styleOverrides: { root: { borderColor: 'var(--mui-palette-divider)' } } },
  /** Plain MUI tables (the pinned-asset list) read like the Data Grid. */
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottomColor: 'var(--mui-palette-surfaces-hairline)' },
      head: {
        fontWeight: 600,
        fontSize: '0.78125rem',
        color: 'var(--mui-palette-text-secondary)',
        borderBottomColor: 'var(--mui-palette-divider)',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: { tooltip: { borderRadius: radius.control, boxShadow: elevation.tooltip } },
  },
};

export const themeOptions: ThemeOptions = {
  eis: berryChrome,
  cssVariables: { colorSchemeSelector: 'data-mui-color-scheme', cssVarPrefix: 'mui' },
  spacing: spacingUnit,
  shape: { borderRadius: radius.control },
  shadows: shadows as ThemeOptions['shadows'],
  typography: {
    fontFamily: typographyTokens.fontFamily,
    htmlFontSize: 16,
    fontSize: 14,
    // Open Sans is self-hosted at 300/400/600; "bold" is 600 everywhere.
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 600,
    h1: { fontSize: '1.625rem', lineHeight: 1.2, fontWeight: 400 },
    h2: { fontSize: '1.25rem', lineHeight: 1.2, fontWeight: 400 },
    /** The card title: `CardHeader` and `DetailsSection`, 18/21.6. */
    h3: { fontSize: '1.125rem', lineHeight: 1.2, fontWeight: 600 },
    h4: { fontSize: '1rem', lineHeight: 1.3, fontWeight: 600 },
    h5: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 600 },
    h6: { fontSize: '0.8125rem', lineHeight: 1.5, fontWeight: 600 },
    subtitle1: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 600 },
    subtitle2: { fontSize: '0.8125rem', lineHeight: 1.5, fontWeight: 600 },
    body1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none' },
    // The design's helper text is 11.2px — under the console's 12px floor, so it stays 12/16.
    caption: { fontSize: '0.75rem', lineHeight: 1.334 },
    overline: { fontSize: '0.75rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none' },
  },
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: {
          main: brand[700],
          light: brand[500],
          dark: brand[800],
          contrastText: neutral[0],
        },
        secondary: {
          main: navChrome.light.panel,
          light: navChrome.light.text,
          dark: navChrome.light.sub,
          contrastText: neutral[0],
        },
        success: { main: semantic.light.success, contrastText: neutral[0] },
        warning: { main: semantic.light.warning, contrastText: neutral[0] },
        error: { main: semantic.light.error, contrastText: neutral[0] },
        info: { main: semantic.light.info, contrastText: neutral[0] },
        background: { default: neutral[50], paper: neutral[0] },
        text: { primary: neutral[900], secondary: neutral[600], disabled: neutral[500] },
        divider: neutral[200],
        status: statusLight,
        surfaces: {
          nav: navChrome.light.panel,
          navSub: navChrome.light.sub,
          toolbar: neutral[0],
          sunken: neutral[50],
          raised: neutral.zebra,
          hairline: neutral[100],
          selected: selection.light.surface,
          selectedText: selection.light.text,
          navText: navChrome.light.text,
          navActive: navChrome.light.active,
          navSection: navChrome.light.heading,
        },
        grey: {
          50: neutral[50],
          100: neutral[100],
          200: neutral[200],
          300: neutral[300],
          400: neutral[400],
          500: neutral[500],
          600: neutral[600],
          700: neutral[700],
          800: neutral[800],
          900: neutral[900],
        },
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        primary: {
          main: brand[300],
          light: brand[500],
          dark: brand[200],
          contrastText: neutral.darkPaper,
        },
        secondary: {
          main: navChrome.dark.text,
          light: neutral[0],
          dark: neutral[400],
          contrastText: neutral.darkPaper,
        },
        success: { main: semantic.dark.success, contrastText: neutral.darkPaper },
        warning: { main: semantic.dark.warning, contrastText: neutral.darkPaper },
        error: { main: semantic.dark.error, contrastText: neutral.darkPaper },
        info: { main: semantic.dark.info, contrastText: neutral.darkPaper },
        background: { default: neutral.darkBackground, paper: neutral.darkPaper },
        text: {
          primary: darkText.primary,
          secondary: darkText.secondary,
          disabled: darkText.disabled,
        },
        divider: neutral.darkLevel1,
        status: statusDark,
        surfaces: {
          nav: navChrome.dark.panel,
          navSub: navChrome.dark.sub,
          toolbar: neutral.darkPaper,
          sunken: neutral.darkBackground,
          raised: neutral.darkLevel2,
          hairline: neutral.darkLevel1,
          selected: selection.dark.surface,
          selectedText: selection.dark.text,
          navText: navChrome.dark.text,
          navActive: navChrome.dark.active,
          navSection: navChrome.dark.heading,
        },
        grey: {
          50: neutral[900],
          100: neutral[800],
          200: neutral[700],
          300: neutral[600],
          400: neutral[500],
          500: neutral[400],
          600: neutral[300],
          700: neutral[200],
          800: neutral[100],
          900: neutral[50],
        },
      },
    },
  },
  components: mergeComponents(densityComponentDefaults, berryComponents),
};

/**
 * Density defaults and a variant's own overrides, one entry per component. A shallow spread would
 * let the variant's `MuiButton` silently discard the shared `defaultProps`; this keeps both.
 */
export function mergeComponents(
  base: NonNullable<ThemeOptions['components']>,
  variant: ThemeOptions['components'],
): ThemeOptions['components'] {
  interface ComponentEntry {
    readonly defaultProps?: object;
    readonly styleOverrides?: object;
  }
  type ComponentMap = Record<string, ComponentEntry | undefined>;
  // MUI's `Components` is an interface (no index signature), so it is read through a map view.
  const baseMap = base as unknown as ComponentMap;
  const variantMap = (variant ?? {}) as unknown as ComponentMap;
  const merged: ComponentMap = { ...baseMap };
  for (const name of Object.keys(variantMap)) {
    const existing: ComponentEntry = merged[name] ?? {};
    const incoming: ComponentEntry = variantMap[name] ?? {};
    merged[name] = {
      ...existing,
      ...incoming,
      defaultProps: { ...existing.defaultProps, ...incoming.defaultProps },
      styleOverrides: { ...existing.styleOverrides, ...incoming.styleOverrides },
    };
  }
  return merged;
}

/** The `berry` theme — the console's own look, the EIS Design System 2025 since T-31. */
export const eisTheme: Theme = createTheme(themeOptions);

export { density, radius, neutral, brand, spacingUnit, elevation };
export type { StatusColor, StatusToken } from './tokens/palette';
